import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  getAllExpenseCategories,
  addCustomExpenseCategory,
  deleteCustomExpenseCategory,
  isCustomExpenseCategory,
} from "../../lib/categoryUtils";
import { Expense, Debt, Income, ExpensePriority } from "../../lib/types";
import * as expenseUtils from "../../lib/expenseUtils";
import {
  TaxProfile,
  getMonthlyTaxFromProfile,
  loadTaxProfiles,
} from "../../lib/taxCalculatorUtils";
import {
  Autocomplete,
  TextField,
  useTheme,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PaymentsIcon from "@mui/icons-material/Payments";
import LinkIcon from "@mui/icons-material/Link";
import RecordPaymentDialog from "./RecordPaymentDialog";
import { expensePaymentAPI, ExpensePayment } from "../../services/api/api";
import CreatedViaBadge from "../common/CreatedViaBadge";
interface ExpenseFormProps {
  onAddExpense: (expense: Omit<Expense, "id">) => Promise<void>;
  onUpdateExpense?: (expense: Expense) => Promise<void>;
  onPaidStatusChange?: (
    expenseId: string,
    update: { isPaid: boolean; paymentsTotal: number }
  ) => void;
  expenseToEdit?: Expense;
  isEditing?: boolean;
  debts?: Debt[]; // Available debts for linking
  income?: Income[];
  taxProfiles?: TaxProfile[];
  expenseProfiles?: { id: string; name: string }[];
  defaultExpenseProfileId?: string;
  onEditLinkedDebt?: (debtId: string) => void;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  onAddExpense,
  onUpdateExpense,
  onPaidStatusChange,
  expenseToEdit,
  isEditing = false,
  debts = [],
  income = [],
  taxProfiles: taxProfilesProp,
  expenseProfiles = [],
  defaultExpenseProfileId,
  onEditLinkedDebt,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [invoicedDate, setInvoicedDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState("monthly");
  const [suspensionPeriodValue, setSuspensionPeriodValue] = useState<
    number | ""
  >("");
  const [suspensionPeriodUnit, setSuspensionPeriodUnit] = useState<
    "days" | "months"
  >("months");
  const [vendor, setVendor] = useState("");
  const [internalCategory, setInternalCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [payments, setPayments] = useState<ExpensePayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [linkedDebtId, setLinkedDebtId] = useState<string>("");
  const [useLinkedDebtAmount, setUseLinkedDebtAmount] = useState(false);
  const [billOrBudget, setBillOrBudget] = useState<"bill" | "budget">("bill");
  const [priority, setPriority] = useState<ExpensePriority | "">("");
  const [isPostponed, setIsPostponed] = useState(false);
  const [calculationType, setCalculationType] = useState<
    "fixed" | "percent_income" | "tax_profile"
  >("fixed");
  const [percentOfIncome, setPercentOfIncome] = useState("");
  const [incomeBasis, setIncomeBasis] = useState<"actual" | "projected">(
    "projected"
  );
  const [taxProfileId, setTaxProfileId] = useState("");
  const [taxProfileName, setTaxProfileName] = useState("");
  const [expensesProfileId, setExpensesProfileId] = useState<string>("");

  const [error, setError] = useState("");
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [pendingCategory, setPendingCategory] = useState("");
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [suspensionEnabled, setSuspensionEnabled] = useState(true);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [taxProfiles, setTaxProfiles] = useState<TaxProfile[]>(
    taxProfilesProp || []
  );

  // Load categories on component mount
  useEffect(() => {
    setCustomCategories(getAllExpenseCategories());
  }, []);

  useEffect(() => {
    if (taxProfilesProp) {
      setTaxProfiles(taxProfilesProp);
      return;
    }
    setTaxProfiles(loadTaxProfiles());
  }, [taxProfilesProp]);

  // If we're editing, populate the form with the expense data
  useEffect(() => {
    if (isEditing && expenseToEdit) {
      setName(typeof expenseToEdit.name === "string" ? expenseToEdit.name : "");
      setAmount(
        expenseToEdit.amount !== undefined && expenseToEdit.amount !== null
          ? String(expenseToEdit.amount)
          : ""
      );

      if (expenseToEdit.invoicedDate) {
        const invoiceDate = new Date(expenseToEdit.invoicedDate);
        const formattedInvoiceDate = invoiceDate.toISOString().split("T")[0];
        setInvoicedDate(formattedInvoiceDate);
      } else {
        setInvoicedDate("");
      }

      // Format the date to YYYY-MM-DD for the date input
      if (expenseToEdit.dueDate) {
        const date = new Date(expenseToEdit.dueDate);
        const formattedDate = date.toISOString().split("T")[0];
        setDueDate(formattedDate);
      } else {
        setDueDate("");
      }

      setIsRecurring(expenseToEdit.isRecurring);
      if (expenseToEdit.recurrencePattern) {
        setRecurrencePattern(expenseToEdit.recurrencePattern);
      }
      setVendor(
        typeof expenseToEdit.vendor === "string" ? expenseToEdit.vendor : ""
      );
      setInternalCategory(
        typeof expenseToEdit.internalCategory === "string"
          ? expenseToEdit.internalCategory
          : ""
      );
      setNotes(
        typeof expenseToEdit.notes === "string" ? expenseToEdit.notes : ""
      );
      setIsPaid(expenseToEdit.isPaid);
      setLinkedDebtId(expenseToEdit.linkedDebtId || "");
      setUseLinkedDebtAmount(
        expenseToEdit.linkedDebtId
          ? expenseToEdit.useLinkedDebtAmount !== false
          : false
      );
      setBillOrBudget(expenseToEdit.billOrBudget || "bill");
      setPriority(expenseToEdit.priority || "");
      setIsPostponed(Boolean(expenseToEdit.isPostponed));
      setCalculationType(expenseToEdit.calculationType || "fixed");
      setPercentOfIncome(
        expenseToEdit.percentOfIncome !== undefined
          ? String(expenseToEdit.percentOfIncome)
          : ""
      );
      setIncomeBasis(expenseToEdit.incomeBasis || "projected");
      setTaxProfileId(expenseToEdit.taxProfileId || "");
      setTaxProfileName(expenseToEdit.taxProfileName || "");
      setExpensesProfileId(expenseToEdit.expensesProfileId || "");

      // Load suspension period if exists
      console.debug("[ExpenseForm] Loading expense for edit:", {
        id: expenseToEdit.id,
        name: expenseToEdit.name,
        isRecurring: expenseToEdit.isRecurring,
        suspensionPeriodValue: expenseToEdit.suspensionPeriodValue,
        suspensionPeriodUnit: expenseToEdit.suspensionPeriodUnit,
        billOrBudget: expenseToEdit.billOrBudget,
        calculationType: expenseToEdit.calculationType,
        percentOfIncome: expenseToEdit.percentOfIncome,
        incomeBasis: expenseToEdit.incomeBasis,
        taxProfileId: expenseToEdit.taxProfileId,
        taxProfileName: expenseToEdit.taxProfileName,
      });
      if (
        expenseToEdit.suspensionPeriodValue !== undefined &&
        expenseToEdit.suspensionPeriodUnit
      ) {
        setSuspensionPeriodValue(expenseToEdit.suspensionPeriodValue);
        setSuspensionPeriodUnit(expenseToEdit.suspensionPeriodUnit);
        setSuspensionEnabled(true);
      } else if (expenseToEdit.isRecurring) {
        // Default to disabled for recurring expenses without suspension set
        setSuspensionEnabled(false);
        setSuspensionPeriodValue("");
        setSuspensionPeriodUnit("months");
      }
    }
  }, [isEditing, expenseToEdit]);

  useEffect(() => {
    if (isEditing) return;
    if (expenseToEdit) return;
    if (typeof defaultExpenseProfileId !== "string") return;
    setExpensesProfileId(defaultExpenseProfileId);
  }, [defaultExpenseProfileId, expenseToEdit, isEditing]);

  const loadPayments = React.useCallback(async () => {
    if (!isEditing || !expenseToEdit?.id) return;
    setPaymentsLoading(true);
    try {
      console.log("[ExpenseForm] Loading payments for", expenseToEdit.id);
      const result = await expensePaymentAPI.list(expenseToEdit.id);
      setPayments(result);
    } catch (err) {
      console.error("[ExpenseForm] Failed to load payments", err);
    } finally {
      setPaymentsLoading(false);
    }
  }, [isEditing, expenseToEdit?.id]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const paymentsTotal = useMemo(
    () => payments.reduce((sum, p) => sum + Number(p.amount || 0), 0),
    [payments]
  );
  const expenseAmount = Number(expenseToEdit?.amount || 0);
  const remainingBalance = Math.max(0, expenseAmount - paymentsTotal);

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      console.log("[ExpenseForm] Deleting payment", paymentId);
      const result = await expensePaymentAPI.remove(paymentId);
      setPayments((prev) => prev.filter((p) => p.id !== paymentId));
      if (result.status && expenseToEdit?.id) {
        setIsPaid(result.status.isPaid);
        onPaidStatusChange?.(expenseToEdit.id, {
          isPaid: result.status.isPaid,
          paymentsTotal: Number(result.status.totalPaid || 0),
        });
      }
    } catch (err) {
      console.error("[ExpenseForm] Failed to delete payment", err);
    }
  };

  const handlePaymentSaved = async () => {
    setPaymentDialogOpen(false);
    if (!expenseToEdit?.id) return;
    try {
      const fresh = await expensePaymentAPI.list(expenseToEdit.id);
      setPayments(fresh);
      const total = fresh.reduce((s, p) => s + Number(p.amount || 0), 0);
      const fullyPaid = total >= expenseAmount && expenseAmount > 0;
      if (fullyPaid !== isPaid) setIsPaid(fullyPaid);
      onPaidStatusChange?.(expenseToEdit.id, {
        isPaid: fullyPaid,
        paymentsTotal: total,
      });
    } catch (err) {
      console.error("[ExpenseForm] Post-save status refresh failed", err);
    }
  };

  // Initialize suspension period when suspension warnings are enabled.
  useEffect(() => {
    if (suspensionEnabled && suspensionPeriodValue === "") {
      setSuspensionPeriodValue(1);
      setSuspensionPeriodUnit("months");
    }
  }, [suspensionEnabled, suspensionPeriodValue]);

  // Auto-populate amount when debt sync is enabled.
  useEffect(() => {
    if (calculationType !== "fixed") return;
    if (linkedDebtId && useLinkedDebtAmount && debts.length > 0) {
      const linkedDebt = debts.find((debt) => debt.id === linkedDebtId);
      if (linkedDebt && Number(linkedDebt.maxMonthlyPayment || 0) > 0) {
        setAmount(
          Math.abs(Number(linkedDebt.maxMonthlyPayment || 0)).toString()
        );
      }
    }
  }, [calculationType, linkedDebtId, useLinkedDebtAmount, debts]);

  useEffect(() => {
    if (calculationType !== "fixed" && linkedDebtId) {
      setLinkedDebtId("");
      setUseLinkedDebtAmount(false);
    }
  }, [calculationType, linkedDebtId]);

  useEffect(() => {
    if (!linkedDebtId) {
      setUseLinkedDebtAmount(false);
    }
  }, [linkedDebtId]);

  // A debt-linked expense IS the debt: its name should be the creditor.
  useEffect(() => {
    if (!linkedDebtId) return;
    const linkedDebt = debts.find((debt) => debt.id === linkedDebtId);
    const creditor = linkedDebt?.creditor || linkedDebt?.account;
    if (creditor) setName(creditor);
  }, [linkedDebtId, debts]);

  const incomeTotals = useMemo(() => {
    const actual = expenseUtils.getCurrentMonthActualIncomeTotal(income);
    const projected = expenseUtils.getCurrentMonthProjectedIncomeTotal(income);
    return { actual, projected };
  }, [income]);

  const selectedTaxProfile = useMemo(() => {
    if (!taxProfileId) return null;
    return (
      taxProfiles.find((profile) => profile.id === taxProfileId) ||
      taxProfiles.find((profile) => profile.name === taxProfileName) ||
      null
    );
  }, [taxProfileId, taxProfileName, taxProfiles]);

  useEffect(() => {
    if (!taxProfileId) return;
    const profile = taxProfiles.find((p) => p.id === taxProfileId);
    if (profile && profile.name !== taxProfileName) {
      setTaxProfileName(profile.name);
    }
  }, [taxProfileId, taxProfileName, taxProfiles]);

  const computedAmountPreview = useMemo(() => {
    if (calculationType === "percent_income") {
      const percentValue = Number(percentOfIncome || 0);
      const base =
        incomeBasis === "actual" ? incomeTotals.actual : incomeTotals.projected;
      return (base * percentValue) / 100;
    }
    if (calculationType === "tax_profile") {
      return selectedTaxProfile
        ? getMonthlyTaxFromProfile(selectedTaxProfile)
        : 0;
    }
    return Number(amount || 0);
  }, [
    calculationType,
    percentOfIncome,
    incomeBasis,
    incomeTotals,
    selectedTaxProfile,
    amount,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requiresAmount = calculationType === "fixed";
    const safeName = String(name || "");
    const safeVendor = String(vendor || "");
    const safeInternalCategory = String(internalCategory || "");
    const missingRequiredFields =
      !safeName.trim() ||
      !dueDate ||
      !safeVendor.trim() ||
      !safeInternalCategory.trim() ||
      (requiresAmount && !amount) ||
      (calculationType === "percent_income" && !percentOfIncome) ||
      (calculationType === "tax_profile" && !taxProfileId);
    if (missingRequiredFields) {
      setShowRequiredErrors(true);
      setError("Please fill in all required fields");
      return;
    }
    if (invoicedDate && new Date(invoicedDate) >= new Date(dueDate)) {
      setError("Invoiced Date must be before the Due Date");
      return;
    }
    setShowRequiredErrors(false);
    setError("");
    setIsSaving(true);

    const amountValue = calculationType === "fixed" ? parseFloat(amount) : 0;

    const expenseData: Partial<Expense> = {
      name,
      amount: amountValue,
      invoicedDate: invoicedDate ? new Date(invoicedDate) : undefined,
      dueDate: new Date(dueDate),
      isPaid: isEditing ? isPaid : false,
      isRecurring,
      vendor,
      internalCategory,
      notes: notes.trim() || undefined,
      expensesProfileId: expensesProfileId || undefined,
      linkedDebtId:
        calculationType === "fixed" ? linkedDebtId || undefined : undefined,
      useLinkedDebtAmount:
        calculationType === "fixed" && linkedDebtId
          ? useLinkedDebtAmount
          : false,
      billOrBudget,
      priority: priority || undefined,
      isPostponed,
      calculationType,
      percentOfIncome:
        calculationType === "percent_income"
          ? Number(percentOfIncome || 0)
          : undefined,
      incomeBasis:
        calculationType === "percent_income" ? incomeBasis : undefined,
      taxProfileId:
        calculationType === "tax_profile" ? taxProfileId : undefined,
      taxProfileName:
        calculationType === "tax_profile" ? taxProfileName : undefined,
    };

    if (isRecurring) {
      expenseData.recurrencePattern = recurrencePattern;
    }
    // Allow suspension warnings for both recurring and one-time expenses.
    if (
      suspensionEnabled &&
      suspensionPeriodValue !== "" &&
      suspensionPeriodValue > 0
    ) {
      expenseData.suspensionPeriodValue = suspensionPeriodValue;
      expenseData.suspensionPeriodUnit = suspensionPeriodUnit;
    }

    try {
      if (isEditing && expenseToEdit && onUpdateExpense) {
        // If editing, update the existing expense
        const updatedExpense: Expense = {
          ...expenseToEdit,
          name: name,
          amount: amountValue,
          invoicedDate: invoicedDate ? new Date(invoicedDate) : undefined,
          dueDate: new Date(dueDate),
          isPaid: isPaid,
          isRecurring: isRecurring,
          vendor: vendor,
          internalCategory: internalCategory,
          notes: notes.trim() || undefined,
          expensesProfileId: expensesProfileId || undefined,
          linkedDebtId:
            calculationType === "fixed" ? linkedDebtId || undefined : undefined,
          useLinkedDebtAmount:
            calculationType === "fixed" && linkedDebtId
              ? useLinkedDebtAmount
              : false,
          billOrBudget,
          priority: priority || undefined,
          isPostponed,
          calculationType,
          percentOfIncome:
            calculationType === "percent_income"
              ? Number(percentOfIncome || 0)
              : undefined,
          incomeBasis:
            calculationType === "percent_income" ? incomeBasis : undefined,
          taxProfileId:
            calculationType === "tax_profile" ? taxProfileId : undefined,
          taxProfileName:
            calculationType === "tax_profile" ? taxProfileName : undefined,
        };

        if (isRecurring) {
          updatedExpense.recurrencePattern = recurrencePattern;
          updatedExpense.isRecurringInstance = false;
        }
        // Allow suspension warnings for both recurring and one-time expenses.
        if (
          suspensionEnabled &&
          suspensionPeriodValue !== "" &&
          suspensionPeriodValue > 0
        ) {
          updatedExpense.suspensionPeriodValue = suspensionPeriodValue;
          updatedExpense.suspensionPeriodUnit = suspensionPeriodUnit;
        }

        console.debug("[ExpenseForm] Updating expense with suspension data:", {
          id: updatedExpense.id,
          name: updatedExpense.name,
          isRecurring: updatedExpense.isRecurring,
          suspensionPeriodValue: updatedExpense.suspensionPeriodValue,
          suspensionPeriodUnit: updatedExpense.suspensionPeriodUnit,
          formStateValue: suspensionPeriodValue,
          formStateUnit: suspensionPeriodUnit,
          expensesProfileId,
          billOrBudget,
          invoicedDate,
          calculationType,
          percentOfIncome,
          incomeBasis,
          taxProfileId,
          taxProfileName,
        });

        await onUpdateExpense(updatedExpense);
      } else {
        // If adding, create a new expense
        console.debug("[ExpenseForm] Creating expense with bill/budget:", {
          name,
          billOrBudget,
          invoicedDate,
          expensesProfileId,
          calculationType,
          percentOfIncome,
          incomeBasis,
          taxProfileId,
          taxProfileName,
        });
        await onAddExpense(expenseData as Omit<Expense, "id">);
      }
      // Parent component will close the form on success
    } catch (err) {
      console.error("[ExpenseForm] Error saving expense:", err);
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Failed to save expense. Please try again.";
      setError(message);
      setIsSaving(false);
    }
  };

  const categoryOptions = customCategories;

  const handleCategoryKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && internalCategory.trim()) {
      e.preventDefault();
      const categoryValue = internalCategory.trim();

      // Check if category already exists (case insensitive)
      const existingCategory = categoryOptions.find(
        (cat) => cat.toLowerCase() === categoryValue.toLowerCase()
      );

      if (!existingCategory) {
        setPendingCategory(categoryValue);
        setShowCategoryModal(true);
      }
    }
  };

  const handleCreateCategory = () => {
    if (pendingCategory) {
      const updatedCategories = addCustomExpenseCategory(pendingCategory);
      setCustomCategories(updatedCategories);
      setInternalCategory(pendingCategory);
    }
    setShowCategoryModal(false);
    setPendingCategory("");
  };

  const handleCancelCategoryCreation = () => {
    setShowCategoryModal(false);
    setPendingCategory("");
  };

  const handleDeleteCategory = (category: string) => {
    deleteCustomExpenseCategory(category);
    // Reload all categories (built-in + custom)
    setCustomCategories(getAllExpenseCategories());
    // Clear selection if the deleted category was selected
    if (internalCategory === category) {
      setInternalCategory("");
    }
    console.log("[ExpenseForm] Deleted category:", category);
  };

  const handleAddNewCategory = () => {
    if (newCategoryInput.trim()) {
      const category = newCategoryInput.trim();
      // Check if category already exists
      if (!customCategories.includes(category)) {
        addCustomExpenseCategory(category);
        setCustomCategories(getAllExpenseCategories());
        setInternalCategory(category);
      }
      setNewCategoryInput("");
    }
  };

  const formStyle = {
    borderRadius: "8px",
    padding: "16px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: isDarkMode ? "1px solid #444" : "1px solid #e2e8f0",
    borderRadius: "4px",
    marginBottom: "8px",
    fontSize: "16px",
    backgroundColor: isDarkMode ? "#2d2d2d" : "white",
    color: isDarkMode ? "#e2e8f0" : "#1a202c",
  };

  const requiredInputErrorStyle: React.CSSProperties = {
    border: "1px solid #E53E3E",
    backgroundColor: isDarkMode ? "rgba(229, 62, 62, 0.15)" : "#FFF5F5",
  };

  const hasRequiredError = (value: string): boolean =>
    showRequiredErrors && !value.trim();

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "4px",
    fontSize: "14px",
    fontWeight: 500,
    color: isDarkMode ? "#e2e8f0" : "#1a202c",
  };

  const buttonStyle = {
    backgroundColor: "#38B2AC",
    color: "white",
    padding: "10px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    width: "100%",
    marginTop: "10px",
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {/* Loading overlay */}
        {isSaving && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDarkMode
                ? "rgba(0, 0, 0, 0.7)"
                : "rgba(255, 255, 255, 0.8)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "8px",
            }}
          >
            <CircularProgress sx={{ color: "#805AD5", mb: 2 }} />
            <span
              style={{
                color: isDarkMode ? "#e2e8f0" : "#4a5568",
                fontWeight: 500,
              }}
            >
              Saving expense...
            </span>
          </Box>
        )}

        <form onSubmit={handleSubmit} style={formStyle}>
          {error && (
            <div
              style={{
                color: "#E53E3E",
                marginBottom: "10px",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          <fieldset
            disabled={isSaving}
            style={{ border: "none", padding: 0, margin: 0 }}
          >
            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Priority</label>
              <select
                value={priority}
                onChange={(e) => {
                  const nextValue = e.target.value as ExpensePriority | "";
                  setPriority(nextValue);
                  console.log("[ExpenseForm] Priority changed:", nextValue);
                }}
                style={inputStyle}
              >
                <option value="">Unassigned</option>
                <option value="emergency">Emergency</option>
                <option value="essential">Essential</option>
                <option value="optional">Optional</option>
              </select>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>
                Expense Name
                {isEditing && expenseToEdit?.createdVia && (
                  <span style={{ marginLeft: 8, verticalAlign: "middle" }}>
                    <CreatedViaBadge createdVia={expenseToEdit.createdVia} />
                  </span>
                )}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Electric Bill"
                style={{
                  ...inputStyle,
                  ...(hasRequiredError(name) ? requiredInputErrorStyle : {}),
                }}
              />
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Amount Type</label>
              <select
                value={calculationType}
                onChange={(e) => {
                  const nextValue = e.target.value as
                    | "fixed"
                    | "percent_income"
                    | "tax_profile";
                  setCalculationType(nextValue);
                  console.log("[ExpenseForm] Amount type changed:", nextValue);
                }}
                style={inputStyle}
              >
                <option value="fixed">Fixed amount</option>
                <option value="percent_income">Percent of income</option>
                <option value="tax_profile">Tax profile</option>
              </select>
            </div>

            {calculationType === "fixed" && (
              <div style={{ marginBottom: "10px" }}>
                <label style={labelStyle}>Amount</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  style={{
                    ...inputStyle,
                    ...(showRequiredErrors && !amount
                      ? requiredInputErrorStyle
                      : {}),
                    backgroundColor: linkedDebtId
                      ? useLinkedDebtAmount
                        ? isDarkMode
                          ? "#1a1a1a"
                          : "#f7fafc"
                        : isDarkMode
                          ? "#2d2d2d"
                          : "white"
                      : isDarkMode
                        ? "#2d2d2d"
                        : "white",
                    cursor:
                      linkedDebtId && useLinkedDebtAmount
                        ? "not-allowed"
                        : "text",
                  }}
                  disabled={Boolean(linkedDebtId && useLinkedDebtAmount)}
                />
                {linkedDebtId && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#718096",
                      marginTop: "4px",
                    }}
                  >
                    {useLinkedDebtAmount
                      ? "Amount is automatically set from the linked debt's monthly payment. "
                      : "Amount is manually overridden for this expense. "}
                    {useLinkedDebtAmount ? (
                      <button
                        type="button"
                        onClick={() => {
                          setUseLinkedDebtAmount(false);
                          console.debug(
                            "[ExpenseForm] Enabled manual amount override",
                            { linkedDebtId, expenseId: expenseToEdit?.id }
                          );
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          marginRight: "4px",
                          color: isDarkMode ? "#63b3ed" : "#3182ce",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        Override amount
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setUseLinkedDebtAmount(true);
                          console.debug(
                            "[ExpenseForm] Restored linked debt amount sync",
                            { linkedDebtId, expenseId: expenseToEdit?.id }
                          );
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          marginRight: "4px",
                          color: isDarkMode ? "#63b3ed" : "#3182ce",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        Use linked debt payment
                      </button>
                    )}
                    {onEditLinkedDebt ? (
                      <button
                        type="button"
                        onClick={() => onEditLinkedDebt(linkedDebtId)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          color: isDarkMode ? "#63b3ed" : "#3182ce",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        Edit linked debt
                      </button>
                    ) : (
                      <Link
                        to={`/debts?editDebtId=${encodeURIComponent(
                          linkedDebtId
                        )}`}
                        style={{
                          color: isDarkMode ? "#63b3ed" : "#3182ce",
                          textDecoration: "underline",
                        }}
                      >
                        Edit linked debt
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {calculationType === "percent_income" && (
              <div style={{ marginBottom: "10px" }}>
                <label style={labelStyle}>Percent of Income</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={percentOfIncome}
                  onChange={(e) => setPercentOfIncome(e.target.value)}
                  placeholder="10"
                  style={{
                    ...inputStyle,
                    ...(showRequiredErrors && !percentOfIncome
                      ? requiredInputErrorStyle
                      : {}),
                  }}
                />
                <label style={labelStyle}>Income Basis</label>
                <select
                  value={incomeBasis}
                  onChange={(e) => {
                    const nextValue = e.target.value as "actual" | "projected";
                    setIncomeBasis(nextValue);
                    console.log(
                      "[ExpenseForm] Income basis changed:",
                      nextValue
                    );
                  }}
                  style={inputStyle}
                >
                  <option value="actual">Actual (month to date)</option>
                  <option value="projected">Projected (current month)</option>
                </select>
                <div
                  style={{
                    fontSize: "12px",
                    color: isDarkMode ? "#718096" : "#718096",
                    marginTop: "4px",
                  }}
                >
                  Estimated amount:{" "}
                  {expenseUtils.formatCurrency(computedAmountPreview)}
                </div>
                {income.length === 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#E53E3E" : "#C53030",
                      marginTop: "4px",
                    }}
                  >
                    No income loaded for current month.
                  </div>
                )}
              </div>
            )}

            {calculationType === "tax_profile" && (
              <div style={{ marginBottom: "10px" }}>
                <label style={labelStyle}>Tax Profile</label>
                <select
                  value={taxProfileId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    setTaxProfileId(nextId);
                    const nextProfile = taxProfiles.find(
                      (profile) => profile.id === nextId
                    );
                    setTaxProfileName(nextProfile?.name || "");
                    console.log("[ExpenseForm] Tax profile changed:", {
                      taxProfileId: nextId,
                      taxProfileName: nextProfile?.name,
                    });
                  }}
                  style={{
                    ...inputStyle,
                    ...(showRequiredErrors && !taxProfileId
                      ? requiredInputErrorStyle
                      : {}),
                  }}
                >
                  <option value="">Select a profile</option>
                  {taxProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    fontSize: "12px",
                    color: isDarkMode ? "#718096" : "#718096",
                    marginTop: "4px",
                  }}
                >
                  Estimated amount:{" "}
                  {expenseUtils.formatCurrency(computedAmountPreview)}
                </div>
                {taxProfiles.length === 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#E53E3E" : "#C53030",
                      marginTop: "4px",
                    }}
                  >
                    No tax profiles found. Create one in Tools → Tax Calculator.
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Bill or Budget</label>
              <select
                value={billOrBudget}
                onChange={(e) => {
                  const nextValue = e.target.value as "bill" | "budget";
                  setBillOrBudget(nextValue);
                  console.log("[ExpenseForm] Bill/Budget changed:", nextValue);
                }}
                style={inputStyle}
              >
                <option value="bill">Bill</option>
                <option value="budget">Budget</option>
              </select>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={isPostponed}
                  onChange={(e) => {
                    setIsPostponed(e.target.checked);
                    console.log(
                      "[ExpenseForm] Postponed changed:",
                      e.target.checked
                    );
                  }}
                />
                <span style={{ fontSize: "14px" }}>
                  Postponed (temporarily not being paid — hidden from the
                  Recurring Expenses view unless included)
                </span>
              </label>
            </div>

            {expenseProfiles.length > 0 && (
              <div style={{ marginBottom: "10px" }}>
                <label style={labelStyle}>Visible In Profile</label>
                <select
                  value={expensesProfileId}
                  onChange={(e) => {
                    setExpensesProfileId(e.target.value);
                    console.log("[ExpenseForm] expensesProfileId changed:", {
                      expensesProfileId: e.target.value,
                    });
                  }}
                  style={inputStyle}
                >
                  <option value="">All profiles (Global)</option>
                  {expenseProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Invoiced Date</label>
              <input
                type="date"
                value={invoicedDate}
                onChange={(e) => {
                  setInvoicedDate(e.target.value);
                  console.log(
                    "[ExpenseForm] Invoiced date changed:",
                    e.target.value
                  );
                }}
                style={{
                  ...inputStyle,
                }}
              />
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{
                  ...inputStyle,
                  ...(showRequiredErrors && !dueDate
                    ? requiredInputErrorStyle
                    : {}),
                }}
              />
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: isDarkMode ? "#e2e8f0" : "#1a202c",
                }}
              >
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={() => setIsRecurring(!isRecurring)}
                  style={{ marginRight: "8px" }}
                />
                Recurring Expense?
              </label>
            </div>

            {isRecurring && (
              <div style={{ marginBottom: "10px" }}>
                <label style={labelStyle}>Recurrence Pattern</label>
                <select
                  value={recurrencePattern}
                  onChange={(e) => setRecurrencePattern(e.target.value)}
                  style={inputStyle}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            )}

            <div style={{ marginBottom: "10px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={suspensionEnabled}
                    onChange={(e) => {
                      setSuspensionEnabled(e.target.checked);
                      if (e.target.checked && suspensionPeriodValue === "") {
                        setSuspensionPeriodValue(1);
                        setSuspensionPeriodUnit("months");
                      }
                    }}
                    size="small"
                    sx={{
                      color: isDarkMode ? "#a0aec0" : "#718096",
                      "&.Mui-checked": { color: "#805AD5" },
                    }}
                  />
                }
                label={
                  <span style={{ ...labelStyle, marginBottom: 0 }}>
                    Enable Suspension Warning
                  </span>
                }
                sx={{ marginBottom: "8px" }}
              />
              {suspensionEnabled && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="number"
                      min={0}
                      value={suspensionPeriodValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSuspensionPeriodValue(
                          val === "" ? "" : parseInt(val, 10)
                        );
                      }}
                      placeholder="1"
                      style={{
                        ...inputStyle,
                        width: "80px",
                        marginBottom: 0,
                      }}
                    />
                    <select
                      value={suspensionPeriodUnit}
                      onChange={(e) =>
                        setSuspensionPeriodUnit(
                          e.target.value as "days" | "months"
                        )
                      }
                      style={{
                        ...inputStyle,
                        width: "120px",
                        marginBottom: 0,
                      }}
                    >
                      <option value="months">Month(s)</option>
                      <option value="days">Day(s)</option>
                    </select>
                    <span
                      style={{
                        color: isDarkMode ? "#a0aec0" : "#718096",
                        fontSize: "14px",
                      }}
                    >
                      after due date
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#718096",
                      marginTop: "4px",
                    }}
                  >
                    Set when service would be suspended/disconnected if unpaid
                  </div>
                </>
              )}
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Vendor</label>
              <input
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="e.g., MidState Electric"
                style={{
                  ...inputStyle,
                  ...(hasRequiredError(vendor) ? requiredInputErrorStyle : {}),
                }}
              />
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>
                Internal Category
                <Tooltip title="Manage Categories" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShowCategoryManager(!showCategoryManager)}
                    sx={{
                      marginLeft: "8px",
                      color: showCategoryManager
                        ? "#805AD5"
                        : isDarkMode
                          ? "#a0aec0"
                          : "#718096",
                    }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </label>
              <Autocomplete
                freeSolo
                options={categoryOptions}
                value={internalCategory}
                onChange={(_, newValue) => {
                  setInternalCategory(newValue || "");
                }}
                onInputChange={(_, newInputValue) => {
                  setInternalCategory(newInputValue);
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{option}</span>
                    {isCustomExpenseCategory(option) && (
                      <Tooltip title="Delete custom category" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(option);
                          }}
                          sx={{
                            color: "#E53E3E",
                            "&:hover": { backgroundColor: "#E53E3E20" },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    onKeyPress={handleCategoryKeyPress}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "16px",
                        backgroundColor: isDarkMode ? "#2d2d2d" : "white",
                        color: isDarkMode ? "#e2e8f0" : "#1a202c",
                        "& fieldset": {
                          borderColor:
                            showRequiredErrors && !internalCategory.trim()
                              ? "#E53E3E"
                              : isDarkMode
                                ? "#444"
                                : "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor:
                            showRequiredErrors && !internalCategory.trim()
                              ? "#E53E3E"
                              : isDarkMode
                                ? "#666"
                                : "#cbd5e0",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: isDarkMode ? "#e2e8f0" : "#1a202c",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: isDarkMode ? "#a0aec0" : "#4a5568",
                  },
                  "& .MuiAutocomplete-clearIndicator": {
                    color: isDarkMode ? "#a0aec0" : "#4a5568",
                  },
                }}
              />
              {/* Add new category inline */}
              {showCategoryManager && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "12px",
                    backgroundColor: isDarkMode ? "#1a1a1a" : "#f7fafc",
                    borderRadius: "6px",
                    border: isDarkMode ? "1px solid #333" : "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddNewCategory();
                        }
                      }}
                      placeholder="New category name..."
                      style={{
                        ...inputStyle,
                        marginBottom: 0,
                        flex: 1,
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCategory}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#38A169",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginTop: "8px",
                    }}
                  >
                    Custom categories can be deleted from the dropdown. Built-in
                    categories cannot be removed.
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Link to Debt (Optional)</label>
              <Autocomplete
                options={debts}
                value={debts.find((d) => d.id === linkedDebtId) || null}
                disabled={calculationType !== "fixed"}
                onChange={(_, newValue) => {
                  setLinkedDebtId(newValue?.id || "");
                  setUseLinkedDebtAmount(Boolean(newValue?.id));
                }}
                getOptionLabel={(debt) =>
                  `${debt.creditor ? `${debt.creditor} - ` : ""}${debt.account} | Bal: $${Number(debt.balance || 0).toFixed(2)} | Payment: $${Number(debt.maxMonthlyPayment || 0).toFixed(2)}`
                }
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  if (!searchTerm) return options;
                  return options.filter((debt) => {
                    const creditor = (debt.creditor || "").toLowerCase();
                    const account = (debt.account || "").toLowerCase();
                    return (
                      creditor.includes(searchTerm) ||
                      account.includes(searchTerm)
                    );
                  });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, debt) => (
                  <li {...props} key={debt.id}>
                    {`${debt.creditor ? `${debt.creditor} - ` : ""}${debt.account} | Bal: $${Number(debt.balance || 0).toFixed(2)} | Payment: $${Number(debt.maxMonthlyPayment || 0).toFixed(2)}`}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      calculationType !== "fixed"
                        ? "Only available for fixed amount"
                        : "Search by creditor or account name..."
                    }
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "16px",
                        backgroundColor: isDarkMode ? "#2d2d2d" : "white",
                        color: isDarkMode ? "#e2e8f0" : "#1a202c",
                        "& fieldset": {
                          borderColor: isDarkMode ? "#444" : "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: isDarkMode ? "#666" : "#cbd5e0",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: isDarkMode ? "#e2e8f0" : "#1a202c",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: isDarkMode ? "#718096" : "#a0aec0",
                        opacity: 1,
                      },
                    }}
                  />
                )}
                sx={{
                  marginBottom: "0",
                  "& .MuiAutocomplete-popupIndicator": {
                    color: isDarkMode ? "#a0aec0" : "#4a5568",
                  },
                  "& .MuiAutocomplete-clearIndicator": {
                    color: isDarkMode ? "#a0aec0" : "#4a5568",
                  },
                }}
              />
              {linkedDebtId && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#718096",
                    marginTop: "4px",
                  }}
                >
                  {useLinkedDebtAmount
                    ? "Amount will be automatically set to the debt's monthly payment. "
                    : "Amount is manually overridden while staying linked to this debt. "}
                  {useLinkedDebtAmount ? (
                    <button
                      type="button"
                      onClick={() => {
                        setUseLinkedDebtAmount(false);
                        console.debug(
                          "[ExpenseForm] Enabled manual amount override",
                          { linkedDebtId, expenseId: expenseToEdit?.id }
                        );
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        marginRight: "4px",
                        color: isDarkMode ? "#63b3ed" : "#3182ce",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Override amount
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setUseLinkedDebtAmount(true);
                        console.debug(
                          "[ExpenseForm] Restored linked debt amount sync",
                          { linkedDebtId, expenseId: expenseToEdit?.id }
                        );
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        marginRight: "4px",
                        color: isDarkMode ? "#63b3ed" : "#3182ce",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Use linked debt payment
                    </button>
                  )}
                  <Link
                    to={`/debts?editDebtId=${encodeURIComponent(linkedDebtId)}`}
                    onClick={() =>
                      console.debug(
                        "[ExpenseForm] Navigate to linked debt edit",
                        { linkedDebtId }
                      )
                    }
                    style={{
                      color: isDarkMode ? "#63b3ed" : "#3182ce",
                      textDecoration: "underline",
                    }}
                  >
                    Edit linked debt
                  </Link>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional details about this expense..."
                style={{
                  ...inputStyle,
                  minHeight: "80px",
                  resize: "vertical",
                }}
              />
            </div>
          </fieldset>

          {isEditing && expenseToEdit?.id && (
            <fieldset
              style={{
                border: `1px solid ${isDarkMode ? "#2d2d2d" : "#E2E8F0"}`,
                borderRadius: "6px",
                padding: "12px 16px",
                marginBottom: "16px",
              }}
            >
              <legend
                style={{
                  padding: "0 6px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isDarkMode ? "#a0aec0" : "#4A5568",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <PaymentsIcon fontSize="inherit" />
                Payments
              </legend>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                  fontSize: "13px",
                  color: isDarkMode ? "#a0aec0" : "#4A5568",
                }}
              >
                <span>
                  Paid: ${paymentsTotal.toFixed(2)} of $
                  {expenseAmount.toFixed(2)}
                  {remainingBalance > 0
                    ? ` • $${remainingBalance.toFixed(2)} remaining`
                    : expenseAmount > 0
                      ? " • Fully paid"
                      : ""}
                </span>
                <button
                  type="button"
                  onClick={() => setPaymentDialogOpen(true)}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#3182CE",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "13px",
                  }}
                >
                  <AddIcon fontSize="inherit" /> Record Payment
                </button>
              </div>

              {paymentsLoading ? (
                <div
                  style={{
                    fontSize: "13px",
                    color: isDarkMode ? "#a0aec0" : "#718096",
                  }}
                >
                  Loading payments…
                </div>
              ) : payments.length === 0 ? (
                <div
                  style={{
                    fontSize: "13px",
                    fontStyle: "italic",
                    color: isDarkMode ? "#a0aec0" : "#718096",
                  }}
                >
                  No payments recorded yet.
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        color: isDarkMode ? "#a0aec0" : "#4A5568",
                        textAlign: "left",
                      }}
                    >
                      <th style={{ padding: "4px 6px" }}>Date</th>
                      <th style={{ padding: "4px 6px" }}>Amount</th>
                      <th style={{ padding: "4px 6px" }}>Method</th>
                      <th style={{ padding: "4px 6px" }}>Transaction</th>
                      <th style={{ padding: "4px 6px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr
                        key={p.id}
                        style={{
                          borderTop: `1px solid ${
                            isDarkMode ? "#2d2d2d" : "#EDF2F7"
                          }`,
                        }}
                      >
                        <td style={{ padding: "6px" }}>
                          {new Date(p.payment_date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: "6px" }}>
                          ${Number(p.amount).toFixed(2)}
                        </td>
                        <td style={{ padding: "6px" }}>
                          {p.payment_method || "—"}
                        </td>
                        <td style={{ padding: "6px" }}>
                          {p.linked_transaction_id ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title={p.linked_transaction_id}
                            >
                              <LinkIcon fontSize="inherit" /> Linked
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={{ padding: "6px", textAlign: "right" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePayment(p.id)}
                            aria-label="Delete payment"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </fieldset>
          )}

          <button
            type="submit"
            style={{
              ...buttonStyle,
              opacity: isSaving ? 0.6 : 1,
              cursor: isSaving ? "not-allowed" : "pointer",
            }}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : isEditing
                ? "Update Expense"
                : "Add Expense"}
          </button>
        </form>
      </Box>

      {isEditing && expenseToEdit?.id && (
        <RecordPaymentDialog
          open={paymentDialogOpen}
          expenseId={expenseToEdit.id}
          remainingBalance={remainingBalance}
          onCancel={() => setPaymentDialogOpen(false)}
          onSaved={handlePaymentSaved}
        />
      )}

      {/* Category Creation Modal */}
      {showCategoryModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              padding: "24px",
              borderRadius: "8px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                color: isDarkMode ? "#e2e8f0" : "#2D3748",
              }}
            >
              Create New Category
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                color: isDarkMode ? "#a0aec0" : "#4A5568",
              }}
            >
              The category "{pendingCategory}" doesn't exist. Would you like to
              create it?
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCancelCategoryCreation}
                style={{
                  padding: "8px 16px",
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#E2E8F0",
                  color: isDarkMode ? "#a0aec0" : "#4A5568",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCategory}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#3182CE",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Create Category
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpenseForm;
